import { Badge, Button, Card, CardContent, CardHeader, CardTitle } from "randmarcomps"
import { OrderDocument as ApiDocument } from "randmar-api-client"
import React from "react"
import { Link } from "react-router-dom"
import Path from "../../routes/Path"
import type { Document as LegacyDocument } from "../../stores/document/Document"
import OpenPDFButton from "../util/OpenPDFButton"
import { ClipboardList, DollarSign, FileText, MapPin, ReceiptText } from "lucide-react"

interface IProps {
  documentNumber: string
  applicationId?: string
  document: LegacyDocument | ApiDocument
}

const isLegacy = (doc: LegacyDocument | ApiDocument): doc is LegacyDocument => "subTotal" in doc

type DocumentKind = "purchase-order" | "requisition" | "reception" | "document"

const getDocumentKind = (documentNumber: string): DocumentKind => {
  if (documentNumber.startsWith("PO") && documentNumber.length === 9) {
    return "purchase-order"
  }
  if (documentNumber.startsWith("RQ") && documentNumber.length === 9) {
    return "requisition"
  }
  if (documentNumber.startsWith("RC") && documentNumber.length === 9) {
    return "reception"
  }
  return "document"
}

const DocumentCard: React.FC<IProps> = ({ documentNumber, applicationId, document: rawDocument }) => {
  const document = React.useMemo(() => {
    if (isLegacy(rawDocument)) {
      return {
        SubTotal: rawDocument.subTotal,
        Location: rawDocument.location,
      }
    }
    return rawDocument
  }, [rawDocument])

  const kind = React.useMemo(() => getDocumentKind(documentNumber), [documentNumber])

  const badge = React.useMemo(() => {
    switch (kind) {
      case "purchase-order":
        return { label: "Purchase order", variant: "success" as const, Icon: FileText }
      case "requisition":
        return { label: "Requisition", variant: "warning" as const, Icon: ClipboardList }
      case "reception":
        return { label: "Reception", variant: "info" as const, Icon: ReceiptText }
      default:
        return { label: "Document", variant: "secondary" as const, Icon: FileText }
    }
  }, [kind])

  const formattedSubTotal = React.useMemo(() => {
    const value = document.SubTotal ?? null
    if (value === null) return "—"
    return new Intl.NumberFormat("en-CA", { style: "currency", currency: "CAD" }).format(value)
  }, [document.SubTotal])

  const location = React.useMemo(() => document.Location?.trim() || "—", [document.Location])

  return (
    <Card className="group rounded-2xl border border-gray-100 bg-white shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:border-[var(--ops-accent)] hover:shadow-md">
      <CardHeader className="p-6 pb-4">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0 space-y-2">
            <Badge
              variant={badge.variant}
              size="sm"
              icon={<badge.Icon className="h-4 w-4" aria-hidden="true" />}
              className="w-fit"
            >
              {badge.label}
            </Badge>
            <Link
              to={Path.document(applicationId, documentNumber)}
              className="block min-w-0 rounded-md text-slate-900 no-underline hover:text-[color:var(--ops-accent)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ops-focus-ring)] focus-visible:ring-offset-2 focus-visible:ring-offset-white"
            >
              <CardTitle className="truncate text-xl font-medium tracking-tight">{documentNumber}</CardTitle>
            </Link>
          </div>
          <div className="flex flex-col gap-2 sm:items-end">
            <div className="flex flex-wrap items-center justify-start gap-2 sm:justify-end">
              <Button asChild variant="outline" size="sm" className="rounded-xl">
                <Link to={Path.document(applicationId, documentNumber)}>View</Link>
              </Button>
              <OpenPDFButton documentNumber={documentNumber} stealth />
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent className="px-6 pb-6 pt-0">
        <dl className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div className="rounded-xl border border-gray-100 bg-[var(--ops-surface)] p-4">
            <dt className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
              <DollarSign className="h-4 w-4" aria-hidden="true" />
              Sub total
            </dt>
            <dd className="mt-1 text-[15px] font-medium text-slate-900">{formattedSubTotal}</dd>
          </div>
          <div className="rounded-xl border border-gray-100 bg-[var(--ops-surface)] p-4">
            <dt className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
              <MapPin className="h-4 w-4" aria-hidden="true" />
              Location
            </dt>
            <dd className="mt-1 truncate text-[15px] font-medium text-slate-900">{location}</dd>
          </div>
        </dl>
      </CardContent>
    </Card>
  )
}

export default DocumentCard
